import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import clsx from "clsx";
import { api } from "../lib/api";
import { Card, CopyButton, PageHeader, Skeleton, fmtTime } from "../components/ui";
import { BrandMark } from "../components/BrandMark";

mermaid.initialize({ startOnLoad: false, theme: "dark", securityLevel: "loose" });

function Mermaid({ code }) {
  const ref = useRef(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const light = document.documentElement.getAttribute("data-theme") === "light";
    mermaid.initialize({ startOnLoad: false, theme: light ? "neutral" : "dark", securityLevel: "loose" });
    const id = `mmd-${Math.random().toString(36).slice(2)}`;
    mermaid.render(id, code).then(({ svg }) => { if (ref.current) ref.current.innerHTML = svg; }).catch((e) => setErr(String(e)));
  }, [code]);
  if (err) return <pre className="cx-form-error text-xs">{err}</pre>;
  return <div ref={ref} className="cx-card cx-card-static p-4 my-4 overflow-x-auto [&_svg]:mx-auto" data-testid="mermaid-diagram" />;
}

const Code = ({ inline, className, children }) => {
  const lang = /language-(\w+)/.exec(className || "")?.[1];
  const text = String(children).replace(/\n$/, "");
  if (inline || (!lang && !text.includes("\n"))) return <code className="cx-mono text-[0.85em] bg-cx-elevated px-1 py-0.5 rounded">{text}</code>;
  if (lang === "mermaid") return <Mermaid code={text} />;
  return (<div className="relative group my-3">
    <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity"><CopyButton text={text} /></div>
    <pre className="cx-mono text-xs bg-cx-elevated border border-cx-border rounded-cx p-4 overflow-x-auto"><code>{text}</code></pre>
  </div>);
};

export default function Docs() {
  const { slug, sub } = useParams();
  const full = sub ? `${slug}/${sub}` : slug;
  const [list, setList] = useState(null);
  const [doc, setDoc] = useState(null);
  useEffect(() => { api("/docs").then(setList); }, []);
  useEffect(() => { const s = full || "guides/getting-started"; setDoc(null); api(`/docs/${s}`).then(setDoc).catch((e) => setDoc({ error: e.message })); }, [full]);
  const cur = full || "guides/getting-started";
  const section = (label, items, testid) => items.length > 0 && (<div className="mb-3" data-testid={testid}><div className="cx-section-label px-2">{label}</div><ul>{items.map((d) => <li key={d.slug}><Link to={`/docs/${d.slug}`} data-testid={`doc-link-${d.slug.replace("/", "-")}`} className={clsx("block px-2 py-1.5 rounded-md text-sm hover:bg-white/[.04]", cur === d.slug ? "bg-cx-emerald/10 text-cx-emerald" : "text-cx-text-secondary")}>{d.title}</Link></li>)}</ul></div>);
  const L = list || [];
  return (<div>
    <PageHeader title={<span className="inline-flex items-center gap-3"><BrandMark size={28} testid="docs-brand-mark" />Documentation</span>} subtitle="Guides written for this app's screens, a glossary of the words we use, the API reference and the product documents." />
    <div className="grid grid-cols-[250px_1fr] gap-6">
      <Card className="!p-2 self-start sticky top-6" data-testid="docs-index">
        {list === null ? <Skeleton className="h-40" /> : (<>
          {section("Guides", L.filter((d) => d.kind === "guide"), "docs-guides")}
          {section("Glossary", L.filter((d) => d.kind === "glossary"), "docs-glossary")}
          <div className="mb-3" data-testid="docs-api"><div className="cx-section-label px-2">API reference</div><a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer" className="block px-2 py-1.5 rounded-md text-sm hover:bg-white/[.04] text-cx-text-secondary" data-testid="doc-link-api">OpenAPI (Swagger) ↗</a></div>
          {section("Reference", L.filter((d) => d.kind === "reference"), "docs-reference")}
        </>)}
      </Card>
      <Card className="min-w-0" data-testid="doc-content">
        {doc === null ? <Skeleton className="h-96" /> : doc.error ? <div className="cx-form-error">{doc.error}</div> : (<>
          <div className="text-xs text-cx-muted mb-4">{doc.slug}.md · updated {fmtTime(doc.updated)}</div>
          <article className="prose-cx">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ code: Code, table: ({ children }) => <div className="overflow-x-auto my-3"><table className="cx-table">{children}</table></div>, a: ({ href, children }) => (href?.startsWith("/") ? <Link to={href} className="text-cx-emerald underline">{children}</Link> : <a href={href} target="_blank" rel="noreferrer" className="text-cx-emerald underline">{children}</a>) }}>{doc.markdown}</ReactMarkdown>
          </article>
        </>)}
      </Card>
    </div>
  </div>);
}
